/**
 * Tipos de validación en el orden mostrado en el editor.
 * Valor en payload: varias reglas separadas por coma; con argumento `TIPO:valor`;
 * OPTIONS se serializa como `OPTIONS:a%b%c` (el editor usa un campo por opción).
 */
export const validationTypes = [
  "REQUIRED",
  "REGEX",
  "MIN_LENGTH",
  "MAX_LENGTH",
  "MIN_VALUE",
  "MAX_VALUE",
  "NUMERIC",
  "EMAIL",
  "PHONE",
  "OPTIONS",
  "DOCUMENT_EXISTS",
  "DOCUMENT_NOT_EXISTS",
  "CLIENT_EXISTS",
  "CELLPHONE_IDENTITY_CONFLICT",
  "CREDIT_EXISTS",
  "DOCUMENT_EXTERNAL_CHECK",
  "PHONE_EXTERNAL_CHECK",
  "MAX_CREDIT_VALUE",
  "MAX_EXTENSION_VALUE",
  "CREDIT_INFO_CHECK",
  "EXPERIAN_MI_DECISOR_VIABILITY",
  "QUOTA_TERM_ALLOWED",
  "EXTENSION_QUOTA_TERM_ALLOWED",
  "OCCUPATION_INDEX_ALLOWED",
  "SECTOR_OCCUPATION_INDEX_ALLOWED",
  "HOUSE_TYPE_INDEX_ALLOWED",
  "GENDER_INDEX_ALLOWED",
  "MARITAL_STATUS_INDEX_ALLOWED",
  "PAGARE",
  "VIDEOLLAMADA",
  "CUSTOM",
  "BACKGROUND_CHECK_START",
  "BACKGROUND_CHECK_RESULT",
  "FRAUD_VALIDATION",
  "AGE_RANGE",
];

/** none | number | text | regex | options | keyValue (texto libre tras el primer :) */
export const VALIDATION_VALUE_KIND = {
  NONE: "none",
  NUMBER: "number",
  TEXT: "text",
  REGEX: "regex",
  OPTIONS: "options",
  KEY_VALUE: "keyValue",
  FRAUD_THRESHOLDS: "fraudThresholds",
};

export const validationTypeMeta = {
  REQUIRED: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Obligatorio" },
  REGEX: { valueKind: VALIDATION_VALUE_KIND.REGEX, label: "Expresión regular" },
  MIN_LENGTH: { valueKind: VALIDATION_VALUE_KIND.NUMBER, label: "Longitud mínima" },
  MAX_LENGTH: { valueKind: VALIDATION_VALUE_KIND.NUMBER, label: "Longitud máxima" },
  MIN_VALUE: { valueKind: VALIDATION_VALUE_KIND.NUMBER, label: "Valor mínimo" },
  MAX_VALUE: { valueKind: VALIDATION_VALUE_KIND.NUMBER, label: "Valor máximo" },
  NUMERIC: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Numérico" },
  EMAIL: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Email" },
  PHONE: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Teléfono" },
  OPTIONS: { valueKind: VALIDATION_VALUE_KIND.OPTIONS, label: "Opciones (ramas)" },
  DOCUMENT_EXISTS: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Documento existe" },
  DOCUMENT_NOT_EXISTS: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Documento no existe" },
  CLIENT_EXISTS: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Cliente existe" },
  CELLPHONE_IDENTITY_CONFLICT: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Conflicto identidad celular",
  },
  CREDIT_EXISTS: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Crédito existe" },
  DOCUMENT_EXTERNAL_CHECK: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Chequeo documento externo",
  },
  PHONE_EXTERNAL_CHECK: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Chequeo teléfono externo",
  },
  MAX_CREDIT_VALUE: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Valor máx. crédito" },
  MAX_EXTENSION_VALUE: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Valor máx. de ampliación",
  },
  CREDIT_INFO_CHECK: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Chequeo info crédito" },
  EXPERIAN_MI_DECISOR_VIABILITY: {
    valueKind: VALIDATION_VALUE_KIND.KEY_VALUE,
    label: "Experian Mi Decisor Viabilidad",
    /** Editor: pregunta explícita en lugar de texto libre */
    prefilterToggle: true,
  },
  QUOTA_TERM_ALLOWED: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Plazo cuota permitido" },
  EXTENSION_QUOTA_TERM_ALLOWED: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Plazo cuota permitido (ampliación)",
  },
  OCCUPATION_INDEX_ALLOWED: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Índice ocupación permitido",
  },
  SECTOR_OCCUPATION_INDEX_ALLOWED: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Índice sector ocupación permitido",
  },
  HOUSE_TYPE_INDEX_ALLOWED: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Índice tipo de vivienda permitido",
  },
  GENDER_INDEX_ALLOWED: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Índice género permitido",
  },
  MARITAL_STATUS_INDEX_ALLOWED: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Índice estado civil permitido",
  },
  PAGARE: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Pagaré" },
  VIDEOLLAMADA: { valueKind: VALIDATION_VALUE_KIND.NONE, label: "Videollamada" },
  CUSTOM: { valueKind: VALIDATION_VALUE_KIND.KEY_VALUE, label: "Regla personalizada", placeholder: "mi_regla" },
  BACKGROUND_CHECK_START: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Antecedentes TusDatos: iniciar consulta",
  },
  BACKGROUND_CHECK_RESULT: {
    valueKind: VALIDATION_VALUE_KIND.NONE,
    label: "Antecedentes TusDatos: revisar resultado",
  },
  FRAUD_VALIDATION: {
    valueKind: VALIDATION_VALUE_KIND.FRAUD_THRESHOLDS,
    label: "Fraude: umbrales de monto y FPD",
  },
  AGE_RANGE: {
    valueKind: VALIDATION_VALUE_KIND.TEXT,
    label: "Rango de edad (rechaza la solicitud)",
    placeholder: "18-69",
  },
};

const validationTypeLabels = Object.fromEntries(
  Object.entries(validationTypeMeta).map(([key, meta]) => [key, meta.label])
);

/** Coincide con placeholders estilo `{{algo}}` dentro del mensaje */
export const MESSAGE_PLACEHOLDER_REGEX = /\{\{[^}]+\}\}/g;

export const TEMPLATE_TEXT = "TEMPLATE_TEXT";
export const TEMPLATE_BUTTONS = "TEMPLATE_BUTTONS";
export const TEMPLATE_WHATSAPP = "TEMPLATE_WHATSAPP";
export const TEMPLATE_DYNAMIC = "TEMPLATE_DYNAMIC";
export const TEMPLATE_FLOW = "TEMPLATE_FLOW";

/** Máximo de botones de respuesta rápida admitidos por WhatsApp */
export const MAX_WHATSAPP_REPLY_BUTTONS = 3;

const messageTypes = [
  TEMPLATE_TEXT,
  TEMPLATE_BUTTONS,
  "TEMPLATE_LIST",
  "TEMPLATE_IMAGE",
  "TEMPLATE_DOCUMENT",
  "TEMPLATE_VIDEO",
  "TEMPLATE_AUDIO",
  "TEMPLATE_LOCATION",
  TEMPLATE_WHATSAPP,
  TEMPLATE_DYNAMIC,
  TEMPLATE_FLOW,
];

/** Etiquetas alineadas con la definición de negocio de cada tipo de plantilla */
const messageTypeLabels = {
  [TEMPLATE_TEXT]: "Texto simple",
  [TEMPLATE_BUTTONS]: "Botones interactivos (máx. 3 en WhatsApp)",
  TEMPLATE_LIST: "Lista interactiva (más de 3 opciones)",
  TEMPLATE_IMAGE: "Imagen con leyenda opcional",
  TEMPLATE_DOCUMENT: "Documento o archivo",
  TEMPLATE_VIDEO: "Video",
  TEMPLATE_AUDIO: "Audio",
  TEMPLATE_LOCATION: "Ubicación",
  [TEMPLATE_WHATSAPP]: "Plantilla aprobada (WhatsApp Business)",
  [TEMPLATE_DYNAMIC]: "Plantilla con parámetros dinámicos",
  [TEMPLATE_FLOW]: "Flujo WhatsApp",
};

/**
 * Claves de negocio; en el mensaje se usan como `{{clave}}` (ver MESSAGE_PLACEHOLDER_REGEX).
 *
 * Las claves terminadas en `_options` (`occupation_options`, `sector_occupation_options`,
 * `house_type_options`, `quota_options`) cumplen un doble rol:
 *   1. Como placeholder en el `templateText`: al renderizar, el backend las sustituye por
 *      la lista cargada (p. ej. "1. Propia\n2. Familiar\n3. Arrendada").
 *   2. Como entrada del campo `variables` del payload: derivada automáticamente desde la
 *      validación del paso (ver `VALIDATION_TO_SPECIAL_VARIABLE`), o — en el caso de
 *      `quota_options` — también cuando el paso usa `{{quota_options}}` como parámetro
 *      (ver `stepUsesQuotaOptionsPlaceholder`).
 *
 * Por eso aparecen en este picker pero NO se duplican en `templateParameters` al armar el
 * payload (ver el filtro defensivo en `whatsappFlowStorage.js`). `value_approve` NO está
 * aquí porque no se inserta desde este picker (se activa con el checkbox manual del bloque
 * "Variables especiales"); aun así es un placeholder válido del mensaje y se incluye en
 * `MESSAGE_PLACEHOLDER_CATALOG` porque el backend sustituye `{{value_approve}}` por su valor.
 */
const messageParameterKeys = [
  "client_name",
  "client_last_name",
  "commerce_name",
  "client_address",
  "document_type",
  "document_number",
  "date_expedition",
  "occupation",
  "occupation_sector",
  "description_occupation",
  "occupation_options",
  "sector_occupation_options",
  "house_type",
  "house_type_options",
  "gender_options",
  "estado_civil_options",
  "city_address",
  "email",
  "place_of_birth",
  "gender",
  "estado_civil",
  "rental_value",
  "dependents",
  "credit_value",
  "quota_value",
  "quota_options",
  "current_balance",
  "max_approved_extension",
  "extension_quota_options",
  "credit_value_detail",
  "interest_rate_detail",
  "fga_rate",
  "fga_value_with_iva",
  "fga_installments_count",
  "life_insurance_rate",
  "quotas_number_detail",
  "quota_value_detail",
  "first_quota_date",
  "last_quota_date",
  "disbursement_date",
  "credit_type",
  "unemployment_insurance_rate",
  "current_client_cellphone",
  "birth",
  "pagare_url",
];

/**
 * Metadatos (ES) para las variables especiales que viajan en el campo `variables` del payload.
 * Las claves `*_options` también funcionan como placeholders del mensaje (se renderizan con
 * la lista cargada desde la API). `value_approve` se activa desde el checkbox manual del
 * bloque "Variables especiales" y también se renderiza en el texto como `{{value_approve}}`.
 */
export const SPECIAL_VARIABLE_META = {
  occupation_options: {
    label: "Opciones de ocupación",
    description: "Listado de ocupaciones disponibles para selección del cliente.",
  },
  sector_occupation_options: {
    label: "Opciones de sector de ocupación",
    description: "Listado de sectores ocupacionales disponibles según la ocupación.",
  },
  house_type_options: {
    label: "Opciones de tipo de vivienda",
    description: "Listado de tipos de vivienda disponibles para selección del cliente.",
  },
  gender_options: {
    label: "Opciones de género",
    description: "Listado de géneros disponibles para selección del cliente.",
  },
  estado_civil_options: {
    label: "Opciones de estado civil",
    description: "Listado de estados civiles disponibles para selección del cliente.",
  },
  quota_options: {
    label: "Opciones de cuota",
    description: "Opciones de plazo y cuota disponibles para el crédito.",
  },
  current_balance: {
    label: "Saldo actual",
    description: "Saldo pendiente actual del crédito.",
  },
  max_approved_extension: {
    label: "Máximo aprobado de ampliación",
    description: "Monto máximo aprobado para la ampliación del crédito.",
  },
  extension_quota_options: {
    label: "Opciones de cuota de ampliación",
    description: "Opciones de plazo y cuota disponibles para la ampliación del crédito.",
  },
  value_approve: {
    label: "Valor aprobado",
    description: "Monto aprobado tras la evaluación crediticia.",
  },
};

/** Título corto y descripción breve (ES) para cada parámetro del mensaje */
const messageParameterMeta = {
  client_name: {
    label: "Nombre del cliente",
    description: "Nombre o nombres de pila según registro.",
  },
  client_last_name: {
    label: "Apellidos del cliente",
    description: "Apellidos completos según documento.",
  },
  commerce_name: {
    label: "Nombre del comercio",
    description: "Nombre del comercio asociado a la conversacion.",
  },
  client_address: {
    label: "Dirección del cliente",
    description: "Ubicación o dirección de residencia/notificación.",
  },
  document_type: {
    label: "Tipo de documento",
    description: "Clase de identificación (ej. CC, CE, pasaporte).",
  },
  document_number: {
    label: "Número de documento",
    description: "Número único del documento de identidad.",
  },
  date_expedition: {
    label: "Fecha de expedición",
    description: "Fecha en que se expidió el documento.",
  },
  occupation: {
    label: "Ocupación",
    description: "Profesión o actividad principal declarada.",
  },
  occupation_sector: {
    label: "Sector de ocupación",
    description: "Rama o sector económico de la ocupación.",
  },
  description_occupation: {
    label: "Descripción de la ocupación",
    description: "Detalle adicional del cargo o actividad.",
  },
  occupation_options: SPECIAL_VARIABLE_META.occupation_options,
  sector_occupation_options: SPECIAL_VARIABLE_META.sector_occupation_options,
  house_type: {
    label: "Tipo de vivienda",
    description: "Propia, arrendada, familiar u otra clasificación.",
  },
  house_type_options: SPECIAL_VARIABLE_META.house_type_options,
  gender_options: SPECIAL_VARIABLE_META.gender_options,
  estado_civil_options: SPECIAL_VARIABLE_META.estado_civil_options,
  city_address: {
    label: "Ciudad",
    description: "Ciudad de residencia del cliente.",
  },
  email: {
    label: "Email",
    description: "Correo electrónico del cliente.",
  },
  place_of_birth: {
    label: "Lugar de nacimiento",
    description: "Ciudad o lugar de nacimiento del cliente.",
  },
  gender: {
    label: "Género",
    description: "Género del cliente.",
  },
  estado_civil: {
    label: "Estado civil",
    description: "Estado civil del cliente.",
  },
  rental_value: {
    label: "Valor del arriendo",
    description: "Canon mensual u otra cuota de arrendamiento.",
  },
  dependents: {
    label: "Personas a cargo",
    description: "Cantidad o referencia de dependientes económicos.",
  },
  credit_value: {
    label: "Valor del crédito",
    description: "Monto total o línea del crédito solicitado.",
  },
  quota_value: {
    label: "Valor de la cuota",
    description: "Cuota periódica estimada del crédito.",
  },
  quota_options: SPECIAL_VARIABLE_META.quota_options,
  current_balance: SPECIAL_VARIABLE_META.current_balance,
  max_approved_extension: SPECIAL_VARIABLE_META.max_approved_extension,
  extension_quota_options: SPECIAL_VARIABLE_META.extension_quota_options,
  credit_value_detail: {
    label: "Valor crédito",
    description: "Valor del crédito. Formato: 1.234.567 (sin decimales).",
  },
  interest_rate_detail: {
    label: "Tasa de interés",
    description: "Tasa de interés del crédito. Formato: 1,5%.",
  },
  fga_rate: {
    label: "Tarifa FGA",
    description: "Tarifa del Fondo de Garantías. Formato: 2%.",
  },
  fga_value_with_iva: {
    label: "Valor FGA con IVA",
    description: "Valor de la FGA con IVA incluido. Formato: 50.000.",
  },
  fga_installments_count: {
    label: "Número de cuotas FGA",
    description: "Cantidad de cuotas del FGA. Formato: 12.",
  },
  life_insurance_rate: {
    label: "Tarifa seguro de vida",
    description: "Tarifa del seguro de vida. Formato: 0,3%.",
  },
  quotas_number_detail: {
    label: "Número de cuotas",
    description: "Cantidad de cuotas del crédito. Formato: 12.",
  },
  quota_value_detail: {
    label: "Valor cuota",
    description: "Valor de cada cuota. Formato: 110.000.",
  },
  first_quota_date: {
    label: "Fecha primera cuota",
    description: "Fecha de la primera cuota. Formato: 15/01/2026.",
  },
  last_quota_date: {
    label: "Fecha última cuota",
    description: "Fecha de la última cuota. Formato: 15/12/2026.",
  },
  disbursement_date: {
    label: "Fecha de desembolso",
    description: "Fecha en que se desembolsa el crédito al comercio. Formato: 20/12/2025.",
  },
  credit_type: {
    label: "Tipo crédito",
    description: "Tipo de crédito. Formato: Consumo.",
  },
  unemployment_insurance_rate: {
    label: "Tarifa seguro de desempleo",
    description: "Tarifa del seguro de desempleo. Formato: 0,1%.",
  },
  current_client_cellphone: {
    label: "Celular cliente",
    description: "Número de celular del cliente en la conversación actual.",
  },
  birth: {
    label: "Año de nacimiento",
    description: "Año de nacimiento del cliente. Formato: 1990 (solo año).",
  },
  pagare_url: {
    label: "URL del pagaré",
    description: "Enlace al documento de pagaré generado para el cliente.",
  },
};

const toMessageParameterOption = (key) => {
  const token = `{{${key}}}`;
  const meta = messageParameterMeta[key];
  return {
    key,
    value: token,
    label: meta?.label ?? key,
    description: meta?.description ?? "",
  };
};

const toCatalogOption = (value, labelOverrides) => ({
  value,
  label: labelOverrides?.[value]
    ? `${value} (${labelOverrides[value]})`
    : value
        .split("_")
        .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
        .join(" "),
});

export const getWhatsappMessageTypes = () =>
  Promise.resolve(messageTypes.map((v) => toCatalogOption(v, messageTypeLabels)));

export const getWhatsappValidationTypes = () =>
  Promise.resolve(validationTypes.map((v) => toCatalogOption(v, validationTypeLabels)));

export const getWhatsappMessageParameters = () =>
  Promise.resolve(messageParameterKeys.map(toMessageParameterOption));

/** Campos permitidos en el builder de condiciones (saveVariable + parámetros de mensaje). */
export const getConditionalFieldOptions = () => {
  const byKey = new Map();
  saveVariableOptions.forEach(({ key, label }) => byKey.set(key, { key, label }));
  messageParameterKeys.forEach((key) => {
    if (!byKey.has(key)) {
      const meta = messageParameterMeta[key];
      byKey.set(key, { key, label: meta?.label ?? key });
    }
  });
  return Array.from(byKey.values()).sort((a, b) =>
    a.label.localeCompare(b.label, "es")
  );
};

export const extractPlaceholdersFromMessage = (message) => {
  if (message == null || String(message).trim() === "") return [];
  const matches = String(message).match(MESSAGE_PLACEHOLDER_REGEX);
  return matches ? Array.from(new Set(matches)) : [];
};

export const normalizePlaceholderToken = (token) => {
  const s = String(token).trim();
  if (s.startsWith("{{") && s.endsWith("}}")) return s;
  if (s) return `{{${s.replace(/^\{\{|\}\}$/g, "")}}}`;
  return s;
};

/**
 * Placeholders presentes en el mensaje que no están en `allowedTokens` (Set o array).
 */
export const getPlaceholdersNotInAllowList = (message, allowedTokens) => {
  const raw = allowedTokens instanceof Set ? allowedTokens : allowedTokens;
  const allow = new Set(
    [...raw].map((token) => normalizePlaceholderToken(token)).filter(Boolean)
  );
  return extractPlaceholdersFromMessage(message).filter((token) => !allow.has(token));
};

export const DEFAULT_MESSAGE_TYPE = TEMPLATE_TEXT;

export const saveVariableOptions = [
  { key: "client_name", label: "Nombre del cliente" },
  { key: "client_last_name", label: "Apellido del cliente" },
  { key: "client_address", label: "Dirección del cliente" },
  { key: "document_type", label: "Tipo de documento" },
  { key: "document_number", label: "Número de documento" },
  { key: "date_expedition", label: "Fecha de expedición" },
  { key: "document_expedition", label: "Lugar de expedición" },
  { key: "occupation", label: "Ocupación" },
  { key: "sector_occupation", label: "Sector de ocupación" },
  { key: "description_occupation", label: "Descripción de ocupación" },
  { key: "client_occupation", label: "Ocupación del cliente" },
  { key: "client_estimated_incomes", label: "Ingresos estimados" },
  { key: "extra_incomes", label: "Ingresos extra" },
  { key: "average_costs", label: "Gastos promedio" },
  { key: "house_type", label: "Tipo de vivienda" },
  { key: "rental_value", label: "Valor de arriendo" },
  { key: "dependents", label: "Dependientes" },
  { key: "credit_value", label: "Valor del crédito" },
  { key: "quota_value", label: "Valor de cuota" },
  { key: "extension_amount", label: "Valor de ampliación de crédito" },
  { key: "quotas_number", label: "Número de cuotas" },
  { key: "current_client_cellphone", label: "Celular cliente" },
  { key: "birth", label: "Año de nacimiento" },
  { key: "city_address", label: "Ciudad" },
  { key: "email", label: "Email" },
  { key: "place_of_birth", label: "Lugar de nacimiento" },
  { key: "gender", label: "Género" },
  { key: "estado_civil", label: "Estado civil" },
];

export const availableActions = [
  { key: "SAVE_CLIENT", label: "Guardar cliente" },
  { key: "SAVE_CREDIT", label: "Guardar Crédito" },
];

export const userOptions = [
  { value: "CLIENT", label: "Cliente" },
  { value: "COMMERCE", label: "Comercio" },
  { value: "ADMIN", label: "Administrador" },
];

export const templateLanguageOptions = [
  { value: "es", label: "Español" },
];

/**
 * Mapeo determinista entre el tipo de validación de un paso y la variable especial
 * (catálogo de opciones) que el paso debe enviar en `variables`.
 * Si una validación nueva requiere una variable, basta con agregarla aquí.
 */
export const VALIDATION_TO_SPECIAL_VARIABLE = {
  OCCUPATION_INDEX_ALLOWED: "occupation_options",
  SECTOR_OCCUPATION_INDEX_ALLOWED: "sector_occupation_options",
  HOUSE_TYPE_INDEX_ALLOWED: "house_type_options",
  GENDER_INDEX_ALLOWED: "gender_options",
  MARITAL_STATUS_INDEX_ALLOWED: "estado_civil_options",
  QUOTA_TERM_ALLOWED: "quota_options",
  // En una ampliación el bot no tiene valor de crédito, sino monto de ampliación: con
  // quota_options el paso responde «No se ha seleccionado un valor de crédito».
  EXTENSION_QUOTA_TERM_ALLOWED: "extension_quota_options",
};

/**
 * Empareja cada variable de respuesta (donde se almacena lo que escribe el cliente)
 * con su variable de lista (que muestra las opciones cargadas desde la API).
 * Se usa para detectar y avisar cuando el admin pone `{{house_type}}` en el mensaje
 * en lugar de `{{house_type_options}}`.
 */
export const ANSWER_TO_OPTIONS_VAR = {
  house_type: "house_type_options",
  gender: "gender_options",
  estado_civil: "estado_civil_options",
  occupation: "occupation_options",
  occupation_sector: "sector_occupation_options",
  quota_value: "quota_options",
};

/**
 * Variables de guardado que el agente solo acepta si el paso valida contra el catálogo
 * cargado: sin la validación, toda respuesta se rechaza y el cliente queda atascado.
 */
export const SAVE_VARIABLE_REQUIRED_VALIDATION = {
  gender: "GENDER_INDEX_ALLOWED",
  estado_civil: "MARITAL_STATUS_INDEX_ALLOWED",
};

/** Devuelve la validación faltante (p. ej. "GENDER_INDEX_ALLOWED") del paso, o null. */
export function missingRequiredValidationForSaveVariable(data) {
  const required = SAVE_VARIABLE_REQUIRED_VALIDATION[data?.saveVariable];
  if (!required) return null;
  const rules = data?.validationRules || [];
  return rules.some((rule) => rule?.type === required) ? null : required;
}

/**
 * Alias de la variable de guardado (`saveVariable` -> `tableEntity`). Normaliza claves
 * heredadas para que el sector de ocupación siempre persista como `sector_occupation`.
 */
export const SAVE_VARIABLE_ALIASES = {
  occupation_sector: "sector_occupation",
  averageCosts: "average_costs",
};

/** Variable derivada que se envía en `variables` cuando el mensaje usa placeholders de detalle de crédito. */
export const CREDIT_DETAIL_VARIABLE = "credit_details";

/**
 * Placeholders de detalle de crédito. Si alguno aparece en el texto del mensaje, el paso
 * debe enviar `CREDIT_DETAIL_VARIABLE` en `variables` para que el backend cargue los datos.
 */
export const CREDIT_DETAIL_PLACEHOLDER_KEYS = [
  "credit_value_detail",
  "interest_rate_detail",
  "fga_rate",
  "fga_value_with_iva",
  "fga_installments_count",
  "life_insurance_rate",
  "quotas_number_detail",
  "quota_value_detail",
  "first_quota_date",
  "last_quota_date",
  "disbursement_date",
  "credit_type",
  "unemployment_insurance_rate",
];

/** Variable especial enviada en `variables` cuando el paso usa opciones de cuota. */
export const QUOTA_OPTIONS_VARIABLE = "quota_options";

export const QUOTA_OPTIONS_TOKEN = "{{quota_options}}";

/**
 * Detecta si el paso usa `{{quota_options}}` como parámetro (mensaje o parameterKeys).
 *
 * @param {{ message?: string, parameterKeys?: string[] }} stepData
 * @returns {boolean}
 */
export const stepUsesQuotaOptionsPlaceholder = ({
  message = "",
  parameterKeys = [],
} = {}) => {
  if (String(message).includes(QUOTA_OPTIONS_TOKEN)) return true;
  return (parameterKeys || []).some(
    (key) => normalizePlaceholderToken(key) === QUOTA_OPTIONS_TOKEN
  );
};

/** Variable derivada que se envía en `variables` cuando el paso usa el saldo actual. */
export const CURRENT_BALANCE_VARIABLE = "current_balance";

export const CURRENT_BALANCE_TOKEN = "{{current_balance}}";

/**
 * Detecta si el paso usa `{{current_balance}}` como parámetro (mensaje o parameterKeys).
 *
 * @param {{ message?: string, parameterKeys?: string[] }} stepData
 * @returns {boolean}
 */
export const stepUsesCurrentBalancePlaceholder = ({
  message = "",
  parameterKeys = [],
} = {}) => {
  if (String(message).includes(CURRENT_BALANCE_TOKEN)) return true;
  return (parameterKeys || []).some(
    (key) => normalizePlaceholderToken(key) === CURRENT_BALANCE_TOKEN
  );
};

/** Variable derivada que se envía en `variables` cuando el paso usa el máximo aprobado de ampliación. */
export const MAX_APPROVED_EXTENSION_VARIABLE = "max_approved_extension";

export const MAX_APPROVED_EXTENSION_TOKEN = "{{max_approved_extension}}";

/**
 * Detecta si el paso usa `{{max_approved_extension}}` como parámetro (mensaje o parameterKeys).
 *
 * @param {{ message?: string, parameterKeys?: string[] }} stepData
 * @returns {boolean}
 */
export const stepUsesMaxApprovedExtensionPlaceholder = ({
  message = "",
  parameterKeys = [],
} = {}) => {
  if (String(message).includes(MAX_APPROVED_EXTENSION_TOKEN)) return true;
  return (parameterKeys || []).some(
    (key) => normalizePlaceholderToken(key) === MAX_APPROVED_EXTENSION_TOKEN
  );
};

/** Variable derivada que se envía en `variables` cuando el paso usa opciones de ampliación. */
export const EXTENSION_QUOTA_OPTIONS_VARIABLE = "extension_quota_options";

export const EXTENSION_QUOTA_OPTIONS_TOKEN = "{{extension_quota_options}}";

/**
 * Detecta si el paso usa `{{extension_quota_options}}` como parámetro
 * (mensaje o parameterKeys).
 *
 * @param {{ message?: string, parameterKeys?: string[] }} stepData
 * @returns {boolean}
 */
export const stepUsesExtensionQuotaOptionsPlaceholder = ({
  message = "",
  parameterKeys = [],
} = {}) => {
  if (String(message).includes(EXTENSION_QUOTA_OPTIONS_TOKEN)) return true;
  return (parameterKeys || []).some(
    (key) => normalizePlaceholderToken(key) === EXTENSION_QUOTA_OPTIONS_TOKEN
  );
};

export const PAGARE_URL_TOKEN = "{{pagare_url}}";

/**
 * Detecta si el paso usa `{{pagare_url}}` sin tener la validación PAGARE configurada.
 *
 * @param {{ message?: string, conditionalMessage?: string, validationRules?: object[] }} stepData
 * @returns {boolean}
 */
export const stepHasVideollamadaValidation = (rules) =>
  Array.isArray(rules) && rules.some((r) => r?.type === "VIDEOLLAMADA");

export const stepMissingPagareValidationForUrl = (stepData) => {
  const rules = stepData?.validationRules || [];
  const hasPagare = rules.some((r) => r?.type === "PAGARE");
  const message = stepData?.message || "";
  const conditionalMessage = stepData?.conditionalMessage || "";
  const usesPagareUrl =
    message.includes(PAGARE_URL_TOKEN) ||
    conditionalMessage.includes(PAGARE_URL_TOKEN);
  return usesPagareUrl && !hasPagare;
};

/** Variables especiales derivadas automáticamente desde `validationRules`. */
export const AUTO_SPECIAL_VARIABLE_KEYS = Object.values(VALIDATION_TO_SPECIAL_VARIABLE);

/** Variables especiales que el usuario activa manualmente (checkbox en el editor). */
export const MANUAL_SPECIAL_VARIABLE_KEYS = ["value_approve"];

/**
 * Conjunto completo de variables especiales (auto + manuales). Se mantiene como
 * referencia única y se usa para filtrar contaminación en `parameterKeys`.
 */
export const SPECIAL_VARIABLE_KEYS = [
  ...AUTO_SPECIAL_VARIABLE_KEYS,
  ...MANUAL_SPECIAL_VARIABLE_KEYS,
  CURRENT_BALANCE_VARIABLE,
  MAX_APPROVED_EXTENSION_VARIABLE,
  EXTENSION_QUOTA_OPTIONS_VARIABLE,
];

/**
 * Tokens `{{key}}` permitidos en el texto del mensaje (catálogo global de validación).
 *
 * Incluye los parámetros del mensaje (`messageParameterKeys`, que alimentan el picker de
 * inserción) y las variables especiales manuales (`MANUAL_SPECIAL_VARIABLE_KEYS`, p. ej.
 * `value_approve`): estas últimas no aparecen en el picker porque se activan con el checkbox
 * del bloque "Variables especiales", pero SÍ son placeholders válidos del mensaje porque el
 * backend sustituye `{{value_approve}}` por su valor (ver `TemplateVariableProcessor` /
 * `DataFetcherAdapter` en prami-agentclient). Las `*_options` ya están en
 * `messageParameterKeys`, así que no se duplican aquí.
 */
export const MESSAGE_PLACEHOLDER_CATALOG = new Set(
  [...messageParameterKeys, ...MANUAL_SPECIAL_VARIABLE_KEYS].map((key) => `{{${key}}}`)
);

/**
 * Placeholders en el mensaje que no pertenecen al catálogo de placeholders permitidos.
 */
export const getInvalidMessagePlaceholders = (message) =>
  extractPlaceholdersFromMessage(message).filter(
    (token) => !MESSAGE_PLACEHOLDER_CATALOG.has(token)
  );

export const FLOW_ROLE_OPTIONS = [
  { value: "PRIMARY", label: "Primario" },
  { value: "SECONDARY", label: "Secundario" },
];

export const flowRoleLabel = (flowRole) => {
  const match = FLOW_ROLE_OPTIONS.find((opt) => opt.value === flowRole);
  return match?.label ?? "—";
};

export const ACTION_TABLE_VALUE_MAP = {
  SAVE_CLIENT: "clients",
  SAVE_CREDIT: "credits",
};
